import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { IDataSourceBase } from '../../datasource/models/IDataSourceInfo';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import WebHttpClient from '../../httpClient/WebHttpClient';
import {getSessionManagementServiceInstance} from '../../sessionManagement/SessionManagementService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { Filter } from '../../ts/helpers/Filter';
import { ContentMapper } from '../../ts/mappers/ContentMapper';
import { FlowBaseInfo } from '../../ts/models/FlowInfo';
import { IViewBase } from '../../ts/models/IViewInfo';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { AddFavoriteRequest } from '../../ts/ServerApi/requests/AddFavoriteRequest';
import { GetFavoritesRequest, IGetFavoritesResult } from '../../ts/ServerApi/requests/GetFavoritesRequest';
import { FavoriteOrdering, OrderFavoritesRequest } from '../../ts/ServerApi/requests/OrderFavoritesRequest';
import { RemoveFavoriteRequest } from '../../ts/ServerApi/requests/RemoveFavoriteRequest';
import { ExploreContentApi_ContentType } from '../../ts/ServerApi/types/IContent';
import { FavoriteItemType, IFavorite } from '../../ts/ServerApi/types/IFavorite';
import { IOrderClause } from '../../ts/ServerApi/types/IOrderClause';
import { IWorkbookBase } from '../../workbook/models/IWorkbookInfo';

class FavoritesResource {

  private fetcher = new WebClientListResourceFetcher(
      GetFavoritesRequest,
      (result: IGetFavoritesResult) => result.favorites);

  public all(): ResourceQuery<IFavorite> {
    let query: ResourceQuery<IFavorite> = new ResourceQuery<IFavorite>(this.fetcher);
    let targetTypes: string[] =  ['WORKBOOK', 'DATASOURCE', 'PROJECT', 'VIEW', 'DATAROLE'];

    if (getSessionManagementServiceInstance().flowsOnSiteEnabled()) {
      targetTypes.push('FLOW');
    }

    if (FeatureFlags.metricsSpaceEnabled) {
      targetTypes.push('METRIC');
    }

    if (FeatureFlags.publishedConnectionContentManagementEnabled) {
      targetTypes.push('PUBLISHED_CONNECTION');
    }

    if (FeatureFlags.useCollectionsFavorites) {
      targetTypes.push('COLLECTION');
    }

    return query.addFilter(Filter.stringFieldInArray('targetType', targetTypes));
  }

  public allForTypesAndSort(
    contentTypes: ExploreContentApi_ContentType[],
    order: IOrderClause[],
  ): ResourceQuery<IFavorite> {
    const targetTypes = contentTypes.map(contentType => ContentMapper.ContentTypeToTargetMap[contentType]);
    return new ResourceQuery<IFavorite>(this.fetcher)
      .withOrder(order)
      .addFilter(Filter.stringFieldInArray('targetType', targetTypes));
  }

  public addView(view: IViewBase): Promise<IResult> {
    return this.add(view.id, 'view');
  }

  public addWorkbook(workbook: IWorkbookBase): Promise<IResult> {
    return this.add(workbook.id, 'workbook');
  }

  public addDatasource(datasource: IDataSourceBase): Promise<IResult> {
    return this.add(datasource.idForFavoriting, 'datasource');
  }

  public addFlow(flow: FlowBaseInfo): Promise<IResult> {
    return this.add(flow.id, 'flow');
  }

  public removeView(view: IViewBase): Promise<IResult> {
    return this.remove(view.id, 'view');
  }

  public removeViewById(id: string): Promise<IResult> {
    return this.remove(id, 'view');
  }

  public removeWorkbook(workbook: IWorkbookBase): Promise<IResult> {
    return this.remove(workbook.id, 'workbook');
  }

  public removeDatasource(datasource: IDataSourceBase): Promise<IResult> {
    return this.remove(datasource.idForFavoriting, 'datasource');
  }

  public removeFlow(flow: FlowBaseInfo): Promise<IResult> {
    return this.remove(flow.id, 'flow');
  }

  public add(objectId: string, objectType: FavoriteItemType): Promise<IResult> {
    return WebHttpClient.sendRequest(new AddFavoriteRequest({
      objectId: objectId,
      objectType: objectType
    }));
  }

  public remove(objectId: string, objectType: FavoriteItemType): Promise<IResult> {
    return WebHttpClient.sendRequest(new RemoveFavoriteRequest({
      objectId: objectId,
      objectType: objectType
    }));
  }

  public orderFavorites(orderings: FavoriteOrdering[]) {
    return WebHttpClient.sendRequest(new OrderFavoritesRequest({ orderings }));
  }

}

export const Favorites = new FavoritesResource();
